import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useContext, useEffect, useState } from 'react';

import { isDev } from '~/dev/utils/isDev';
import { User } from '~/types/User';
import { BootMark, markBootOnce } from '~/utils/bootTiming';
import { captureException } from '~/utils/sentry';

import { FlagsIdentityContext } from './FlagsProvider';

interface Props {
  enableReady: boolean;
  onFailed?: () => void;
  user?: User;
}

export function useFlagsReady({ enableReady, onFailed, user }: Props) {
  const [isReady, setIsReady] = useState(enableReady);
  const ldClient = useLDClient();
  const { setIdentifiedKey } = useContext(FlagsIdentityContext);

  // Depend on the three fields `userToLdUser` actually reads rather than on the
  // `user` object itself. `user` comes from redux and its reference changes as
  // auth data lands (cached profile first, then the revalidated one), which used
  // to re-run `identify()` on every change — three serialized round trips to
  // LaunchDarkly during startup for an identical context.
  const clientId = user?.client;
  const clientName = user?.client_name;
  const subDomain = user?.client_config?.sub_domain;

  useEffect(() => {
    if (!ldClient || clientId === undefined) return;

    const key = clientId.toString();
    ldClient
      .identify({
        key,
        name: clientName,
        custom: {
          client_id: key,
          ...(subDomain !== undefined && { sub_domain: subDomain }),
        },
      })
      .then(() => {
        markBootOnce(BootMark.FirstLdIdentifyEnd);
        setIdentifiedKey?.(key);
        setIsReady(true);
      })
      .catch((error: unknown) => {
        captureException('Boot failed: LaunchDarkly identify', error, {
          reason: 'boot_flags_identify_failed',
        });
        onFailed?.();
      });
  }, [ldClient, clientId, clientName, subDomain, setIdentifiedKey, onFailed]);

  return isReady;
}

/**
 * Whether LaunchDarkly flags have been evaluated for the given client, rather
 * than for the anonymous context LaunchDarkly boots with before sign-in.
 */
export function useAreFlagsIdentifiedForClient(clientId: number | undefined) {
  const { identifiedKey, setIdentifiedKey } = useContext(FlagsIdentityContext);

  if (isDev || !setIdentifiedKey) return true;
  return clientId !== undefined && identifiedKey === clientId.toString();
}
